import React, { useState, useMemo, useRef, useLayoutEffect } from "react";
import { useMutation, useQueryClient } from "react-query";
import { Row, Col } from "reactstrap/lib";
import { Card, Modal, ModalBody } from "reactstrap";
import SendIcon from "@mui/icons-material/Send";
import { FormattedMessage } from "react-intl";
import { IconButton, TextField } from "@mui/material";
import {
  PostWorkCardComment,
  SignWorkCardRegistration,
} from "../../actions/teacherMutations";
import { toast } from "react-toastify";
import { BASE_LOGIN_REDIRECT_URL_NO_LWC } from "../../constants/api.constants";
import moment from "moment";
import { FaRegThumbsUp } from "react-icons/fa";
import { FaRegComment } from "react-icons/fa";
import { PiCaretDoubleRight } from "react-icons/pi";
import { IoSchoolOutline } from "react-icons/io5";

const APLStudentEventCard = React.memo(function APLStudentEventCard({
  item,
  date,
  level,
  periodId,
  objectId,
}) {
  const [showComments, setShowComments] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [showFullText, setShowFullText] = useState(false);
  const [isTextTruncated, setIsTextTruncated] = useState(false);
  const textRef = useRef(null);
  const toggle = () => {
    setShowComments(!showComments);
    setValue("");
  };
  const [value, setValue] = useState("");

  const queryClient = useQueryClient();

  const handleChange = (event) => {
    setValue(event.target.value);
  };

  const postComment = () => {
    if (value !== "") {
      postWorkCardComment.mutate(
        {
          created: moment().format("YYYY-MM-DD HH:mm"),
          text: value,
          workRegistrationId: item.id,
        },
        {
          onSuccess: (data) => {
            item.comments.push(data.comments[0]);
            setValue("");
          },
          throwOnError: true,
          onError: (data) => {
            toast.warn(data.response.data);
          },
        }
      );
    }
  };

  const postWorkCardComment = useMutation(PostWorkCardComment);
  const signWorkCardRegistration = useMutation(SignWorkCardRegistration);

  const handleSignOrUnSignRegistration = () => {
    signWorkCardRegistration.mutate(
      {
        workRegistrationId: item.id,
        signed: !item.signedByTeacher,
      },
      {
        onSuccess: (data) => {
          queryClient.refetchQueries(
            "FetchEvents",
            [level, periodId, objectId],
            {
              active: true,
              exact: true,
            }
          );
        },
        throwOnError: true,
        onError: (data) => {
          toast.warn(data.response.data);
        },
      }
    );
  };


  useLayoutEffect(() => {
    const element = textRef.current;
    if (element) {
      const saved = element.style.cssText;
      element.style.cssText = "";
      const fullHeight = element.scrollHeight;
      element.style.cssText = saved;
      setIsTextTruncated(fullHeight > element.clientHeight);
    }
  }, [item.bodyText]);

  const initials = useMemo(
    () =>
      item.studentName
        .split(" ")
        .map((word) => word[0])
        .join("")
        .toUpperCase(),
    [item.studentName]
  );

  return (
    <>
      <Card key={item.id} className="apl-moment-card shadow-sm my-2 rounded-sm"
        style={{ overflow: "hidden" }}
      >
        {item.signedBySupervisor && (
          <div className="apl-moment-card--corner-ribbon--supervisor">
            <div className="apl-moment-card--corner-ribbon__inner--supervisor">
              <div className="apl-moment-card--corner-ribbon__ribbon--supervisor">
                <FaRegThumbsUp
                  style={{ transform: "translate(-15px, -2px) rotate(-60deg)" }}
                  color={"#f5f5f5"}
                  size={"1rem"}
                />
              </div>
            </div>
          </div>
        )}
        {item.signedByTeacher && (
          <div className="apl-moment-card--corner-ribbon--teacher">
            <div className="apl-moment-card--corner-ribbon__inner--teacher">
              <div className="apl-moment-card--corner-ribbon__ribbon--teacher">
                <IoSchoolOutline
                  style={{ transform: "translate(0, -2px) rotate(-60deg)" }}
                  color={"#f5f5f5"}
                  size={"1rem"}
                />
              </div>
            </div>
          </div>
        )}
        <Col lg={12} style={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <Row className="mt-3">
            <Col style={{ flexGrow: "0" }}>
              <div className="apl-moment-card--story" title={item.studentName}>
                <span className="apl-moment-card--story--initials">
                  {initials}
                </span>
              </div>
            </Col>
            <Col>
              <Row>
                <span className="apl-moment-card--title">{item.title}</span>
              </Row>
              <Row>
                <span className="text-muted">{date.toLocaleDateString()}</span>
              </Row>
            </Col>
          </Row>
          <Row className="py-1">
            <Col className="text-muted">
              <span style={{ verticalAlign: "top" }}>
                {item.workStepComponent.workStepName}
              </span>
              <PiCaretDoubleRight />
              <span style={{ verticalAlign: "top" }}>
                {item.workStepComponent.name}
              </span>
              <PiCaretDoubleRight />
              <span style={{ verticalAlign: "top" }}>
                {item.workStepComponent.workingHours}h
              </span>
            </Col>
          </Row>
          <Row className="py-2">
            <Col
              ref={textRef}
              style={showFullText ? {} : {
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >{item.bodyText}</Col>
          </Row>
          {isTextTruncated && (
            <Row>
              <Col>
                <span
                  className="text-muted"
                  style={{ cursor: "pointer", fontSize: "smaller" }}
                  onClick={() => setShowFullText(!showFullText)}
                >
                  {showFullText ? "Visa mindre" : "Läs mer"}
                </span>
              </Col>
            </Row>
          )}
          {item.images.length > 0 && (
            <Row className="py-3">
              <Col className="mx-auto" style={{ display: "flex", gap: "8px", flexWrap: "wrap", justifyContent: "center" }}>
                {item.images.map((image, i) => (
                  <img
                    key={`${item.id}-thumb-${i}`}
                    src={BASE_LOGIN_REDIRECT_URL_NO_LWC + "/" + image}
                    alt=""
                    loading="lazy"
                    onClick={() => setLightboxImage(BASE_LOGIN_REDIRECT_URL_NO_LWC + "/" + image)}
                    style={{
                      width: "80px",
                      height: "80px",
                      objectFit: "cover",
                      borderRadius: "5px",
                      cursor: "pointer",
                    }}
                  />
                ))}
              </Col>
            </Row>
          )}

          <Row className="py-2">
            <Col>
              <span
                style={{ paddingRight: "1rem" }}
                onClick={() => handleSignOrUnSignRegistration()}
              >
                <FaRegThumbsUp
                  color={item.signedByTeacher ? "17c333" : "black"}
                  size={"1.4rem"}
                />
              </span>
              <span onClick={toggle} style={{ cursor: "pointer" }}>
                <FaRegComment className="text-muted" size={"1.5rem"} />
                <span className="ms-1 text-muted">
                  <FormattedMessage id="DoComment" />
                </span>

                {item.comments.length > 0 && (
                  <span className="apl-moment-card--messageNumbers">
                    {item.comments.length}
                  </span>
                )}
              </span>
            </Col>
          </Row>
          {showComments && (
            <div className="mt-3">
              {item.comments.map((comment) => {
                var commentDate = new Date(comment.commentDate);
                return (
                  <div className="mb-2" key={"comment" + comment.id}>
                    <Row style={{ width: "100%" }}>
                      <Col>
                        <span>
                          <span
                            className="pb-0 mb-0"
                            style={{ fontWeight: "bold" }}
                          >
                            {comment.commenterName}
                          </span>
                          &nbsp;&nbsp;
                          {comment.text}
                        </span>
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <span style={{ fontSize: "smaller" }}>
                          {commentDate.toLocaleDateString()}
                        </span>
                      </Col>
                    </Row>
                  </div>
                );
              })}
              <Row className="mb-2">
                <Col>
                  <TextField
                    size="small"
                    className="mt-2"
                    style={{ width: "100%" }}
                    id="comment"
                    label={<FormattedMessage id="DoComment" />}
                    value={value}
                    onChange={handleChange}
                    InputProps={{
                      endAdornment: (
                        <IconButton onClick={postComment}>
                          <SendIcon />
                        </IconButton>
                      ),
                    }}
                    onKeyPress={(ev) => {
                      if (ev.key === "Enter") {
                        postComment();
                        ev.preventDefault();
                      }
                    }}
                  />
                </Col>
              </Row>
            </div>
          )}
        </Col>
      </Card>
      {lightboxImage && (
        <Modal isOpen toggle={() => setLightboxImage(null)} size="lg" centered>
          <ModalBody className="text-center p-0" onClick={() => setLightboxImage(null)} style={{ cursor: "pointer" }}>
            <img
              src={lightboxImage}
              alt=""
              style={{ width: "100%", objectFit: "contain", borderRadius: "5px" }}
            />
          </ModalBody>
        </Modal>
      )}
    </>
  );
});

export default APLStudentEventCard;
